/**
 * Canonicalizers for the `captureHydrationDiff()` recorder in `pages/_app.tsx`.
 *
 * The recorder byte-diffs the SSR `__next.outerHTML` (read at module-init,
 * BEFORE React hydrates — the verbatim server string) against the at-error
 * snapshot (read AFTER React has started touching the DOM). Anything the
 * browser re-serializes differently between those two moments lands as the
 * "first differing byte" and MASKS the real trigger further down the stream.
 * React only throws #418 on TEXT or STRUCTURAL divergence — never on an
 * attribute's serialization — so every rule here removes a known-benign
 * attribute-level artifact from BOTH sides identically. Add a rule when a
 * `hydration_diff` lands on an attribute; never "fix" the styled element.
 *
 * Gaps found so far (all documented in docs/claude/hydration.md → "Recorder"):
 *   1. `: ` / trailing `;` / `url("x")` quoting drift (2026-06-09)
 *   2. zero-length unit re-serialization, `min-width:0` → `0px` (2026-07-28)
 *   3. `next/image` attribute ORDER (2026-08-04)
 *   4. Safari collapses the four inset longhands into the `inset` shorthand:
 *      `left:0;top:0;right:0;bottom:0` (SSR, what `next/image fill` emits) →
 *      `inset:0` (CSSOM). Every page with a `fill` image has this on its first
 *      such <img>, so on Safari the recorder landed there on every fire and the
 *      real trigger was never visible (2026-08-28, /anfitrion/invita/*).
 */

const INSET_LONGHANDS = ['top', 'right', 'bottom', 'left'] as const

/**
 * Collapse `top/right/bottom/left` into the `inset` shorthand when ALL four
 * longhands are present, mirroring the CSSOM's own serialization so the SSR
 * (longhand) and post-hydration Safari (shorthand) forms compare equal.
 * Runs on an already-compacted declaration list (`prop:value;prop:value`).
 */
const collapseInsetLonghands = (css: string): string => {
  if (!css.includes('inset') && !css.includes('top:')) return css
  const decls = css.split(';')
  const found = new Map<string, string>()
  for (const decl of decls) {
    const colon = decl.indexOf(':')
    if (colon < 0) continue
    const prop = decl.slice(0, colon)
    if ((INSET_LONGHANDS as readonly string[]).includes(prop)) {
      found.set(prop, decl.slice(colon + 1))
    }
  }
  if (found.size !== INSET_LONGHANDS.length) return css

  const [top, right, bottom, left] = INSET_LONGHANDS.map(
    (p) => found.get(p) as string
  )
  // CSS shorthand collapse rules: 1 value when all equal, 2 when vertical and
  // horizontal pairs match, 3 when only left==right, else all 4.
  let shorthand: string
  if (top === right && right === bottom && bottom === left) {
    shorthand = top
  } else if (top === bottom && left === right) {
    shorthand = `${top} ${right}`
  } else if (left === right) {
    shorthand = `${top} ${right} ${bottom}`
  } else {
    shorthand = `${top} ${right} ${bottom} ${left}`
  }

  let emitted = false
  const out: string[] = []
  for (const decl of decls) {
    const prop = decl.slice(0, decl.indexOf(':'))
    if ((INSET_LONGHANDS as readonly string[]).includes(prop)) {
      if (!emitted) {
        out.push(`inset:${shorthand}`)
        emitted = true
      }
      continue
    }
    out.push(decl)
  }
  return out.join(';')
}

/**
 * Normalize every inline `style="…"` attribute so the SSR-verbatim and the
 * CSSOM-re-serialized forms compare equal.
 */
export const canonicalizeStyles = (html: string): string =>
  html.replace(/style="([^"]*)"/g, (_m, css: string) => {
    const canon = css
      .replace(/\s*:\s*/g, ':')
      .replace(/\s*;\s*/g, ';')
      .replace(/;+$/, '')
      // url("x") / url('x') / url(&quot;x&quot;) / url(x) → url(x)
      .replace(
        /url\(\s*(?:&quot;|&#34;|['"])?\s*([^)'"]*?)\s*(?:&quot;|&#34;|['"])?\s*\)/g,
        'url($1)'
      )
      // Zero lengths: the CSSOM re-serializes a unitless `0` with a unit
      // (`min-width:0` → `min-width: 0px`). `\b` keeps `10px` intact.
      .replace(
        /\b0(?:px|em|rem|ex|ch|vw|vh|vmin|vmax|cm|mm|in|pt|pc|q|%)(?![\w%-])/gi,
        '0'
      )
    return `style="${collapseInsetLonghands(canon)}"`
  })

const ATTR = /[^\s=/>]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s/>]+))?/g

/**
 * Sort every start tag's attributes so serialization ORDER drops out of the
 * comparison (React reconciles attributes by name; `next/image` emits `style`
 * before `src` on the server and after it on the client).
 */
export const canonicalizeAttrOrder = (html: string): string =>
  html.replace(
    /<([a-zA-Z][\w-]*)((?:\s+[^\s=/>]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s/>]*))?)+)\s*(\/?)>/g,
    (_m, tag: string, attrs: string, selfClose: string) => {
      const sorted = (attrs.match(ATTR) ?? []).sort().join(' ')
      return `<${tag} ${sorted}${selfClose}>`
    }
  )

/**
 * Gap #5 — strip React's `<!-- -->` text-boundary comments from BOTH sides.
 *
 * These are a SERVER-render-only artifact: React emits them so hydration can
 * re-find text-node boundaries, and a CLIENT render emits none. So the moment
 * #418 triggers a clientRender fallback, every boundary comment on the page
 * disappears from the at-error snapshot — and the byte-diff then reports the
 * FIRST `<!-- -->` in the document as `firstDiffIndex`, every single time,
 * regardless of what actually broke.
 *
 * That is not a small bias, it is a total loss of signal: on
 * /anfitrion/invita/* the reported diff landed on `{currency} · {t(...)}` in
 * Calculator, ~200 elements away from the real culprit (a lazily-imported MUI
 * Slider). Stripping them on both sides makes `firstDiffIndex` mean something
 * again. See docs/claude/hydration.md → "Recorder gap #5".
 */
const stripBoundaryComments = (html: string): string =>
  html.replace(/<!-- -->/g, '')

/** How many text-boundary comments a snapshot still carries. */
export const countBoundaryComments = (html: string): number =>
  (html.match(/<!-- -->/g) ?? []).length

/** Full pipeline the recorder applies to BOTH snapshots before diffing. */
export const canonicalizeHydrationHtml = (html: string): string =>
  canonicalizeAttrOrder(canonicalizeStyles(stripBoundaryComments(html)))

// Start tags only: `</div>` can't match (`/` isn't alpha) and neither can
// `<!--` or a bare `<` in text. The lookahead keeps `<span>` and `<br/>` while
// refusing to treat `<3` as a tag.
const TAG_RE = /<([a-zA-Z][\w-]*)(?=[\s/>])/g

/** Count elements by tag name. Order-, attribute- and comment-independent. */
export const tagCensus = (html: string): Record<string, number> => {
  const out: Record<string, number> = {}
  for (const m of html.matchAll(TAG_RE)) {
    const tag = m[1].toUpperCase()
    out[tag] = (out[tag] ?? 0) + 1
  }
  return out
}

/**
 * The structural difference between the two snapshots, as a compact string
 * like `INPUT:-1,SPAN:-4` (negative = present in SSR, missing at error time).
 *
 * THIS IS THE PRIMARY DIAGNOSTIC — prefer it over `firstDiffIndex`. React only
 * throws #418 on TEXT or STRUCTURAL divergence, and a census is immune to every
 * artifact that has defeated the byte-diff so far: boundary-comment loss,
 * inline-style re-serialization, attribute ORDER, emotion `<style>` relocation.
 * It named the Trigger O culprit in one shot (`SPAN:-4,INPUT:-1` = a MUI
 * Slider) after the byte-diff had pointed at innocent markup for months.
 *
 * Emitted as a single value on purpose: it groups in Datadog, so recurring
 * structural mismatches cluster instead of scattering across byte offsets.
 * Empty string means no element-level difference — then it IS a text mismatch,
 * and the byte window is the right thing to read.
 */
export const censusDelta = (ssrHtml: string, csrHtml: string): string => {
  const before = tagCensus(ssrHtml)
  const after = tagCensus(csrHtml)
  const tags = [...new Set([...Object.keys(before), ...Object.keys(after)])]
  return tags
    .sort()
    .map((tag) => [tag, (after[tag] ?? 0) - (before[tag] ?? 0)] as const)
    .filter(([, d]) => d !== 0)
    .map(([tag, d]) => `${tag}:${d > 0 ? '+' : ''}${d}`)
    .join(',')
}
